// Reusable OAuth "connect account" flow as a Promise.
//
// Mirrors the popup + window.postMessage handshake the Accounts page uses, but
// packaged so any component (e.g. the planner chat) can connect a provider and
// await the result. Kept separate so the existing ConnectedAccountsPage /
// NodeCredentialsSection stay untouched.
//
//   connectProvider('slack').then(() => /* connected */).catch(() => /* cancelled/failed */)

const { request, getApiBasePath, getAccessToken } = require('./apiClient');
const { openGoogleScopePicker } = require('./googleScopePicker');

// The login() providers connectable via popup. Anything else needs a pasted
// key, not this. This list also drives the planner modal's "Connect or manage
// accounts" panel. Uniconta is NOT OAuth — its popup serves a credentials form
// (Company ID + API user + password) instead of an OAuth redirect, but it posts
// back the same "<provider>-auth-success" event, so it plugs into the same flow.
var PROVIDER_LABELS = {
  google: 'Google',
  microsoft: 'Microsoft',
  shopify: 'Shopify',
  slack: 'Slack',
  atlassian: 'Atlassian',
  uniconta: 'Uniconta'
};

// Providers whose popup loads a server-served page directly (no /url round-trip).
// The SPA access token rides in the URL hash so the in-popup form can
// authenticate its POST — mirrors ConnectedAccountsPage's pagePopup flow.
var PAGE_POPUP_PATHS = {
  shopify: '/shopify-auth/connect-page',
  uniconta: '/uniconta-auth/connect-page'
};

function providerLabel(provider) {
  return PROVIDER_LABELS[provider] || String(provider || '');
}

function isOAuthProvider(provider) {
  return Object.prototype.hasOwnProperty.call(PROVIDER_LABELS, provider);
}

// A callback message can be lost when an OAuth provider severs window.opener
// while the popup crosses origins. Keep a safe server-side fingerprint so a
// closed popup can still be recognized as successful after the account row was
// inserted or refreshed. updatedAt also detects reconnecting the same account.
function providerAccountsFingerprint(accounts) {
  return (Array.isArray(accounts) ? accounts : []).map(function(credential) {
    var account = (credential && credential.account) || {};
    return [
      credential && credential.id,
      credential && credential.label,
      credential && credential.connectedAt,
      credential && credential.updatedAt,
      account.shop,
      account.email,
      account.username,
      account.teamId,
      account.accountId,
      account.companyId
    ].map(function(value) { return String(value || ''); }).join('|');
  }).sort().join('||');
}

function getProviderAccountsFingerprint(provider) {
  return request('/connected-accounts').then(function(result) {
    var accounts = (result && result.accounts && result.accounts[provider]) || [];
    return providerAccountsFingerprint(accounts);
  });
}

// Opens the provider's OAuth popup and resolves once the callback page posts
// "<provider>-auth-success". Rejects on error, cancellation (popup closed), or
// an unsupported provider.
function connectProvider(provider) {
  if (!isOAuthProvider(provider)) {
    return Promise.reject(new Error('Unsupported provider: ' + provider));
  }
  // Page-popup providers (Uniconta) open their credentials form directly.
  if (PAGE_POPUP_PATHS[provider]) {
    var pageUrl = getApiBasePath() + PAGE_POPUP_PATHS[provider] +
      '#token=' + encodeURIComponent(getAccessToken() || '');
    return runOAuthPopup(provider, '', pageUrl);
  }
  // Google requires the user to name the connection + pick permissions first.
  // The scope-picker modal (mounted once at the app root) collects those and
  // its confirm is still a user gesture, so opening the popup after it is not
  // blocked. Its name + scopes become query params on the /url request.
  if (provider === 'google') {
    return openGoogleScopePicker().then(
      function(selection) {
        var query = 'name=' + encodeURIComponent(selection.name) +
          '&scopes=' + encodeURIComponent((selection.scopes || []).join(','));
        return runOAuthPopup(provider, query);
      },
      function() { return Promise.reject(new Error('cancelled')); }
    );
  }
  return runOAuthPopup(provider, '');
}

// Opens the provider's auth popup and resolves once the callback page posts
// "<provider>-auth-success". `query` (optional) carries provider-specific
// params, e.g. Google's name + selected scopes. `directUrl` (optional) skips
// the /<provider>-auth/url round-trip and loads that URL straight into the
// popup — used by page-popup providers (Uniconta's credentials form).
function runOAuthPopup(provider, query, directUrl) {
  return new Promise(function(resolve, reject) {
    // Start this before navigating the popup. It preserves the pre-login state
    // without delaying window.open (which must stay inside the user gesture).
    var baselineFingerprint = getProviderAccountsFingerprint(provider)
      .catch(function() { return null; });
    var popupName = provider + '-auth';
    var popup = null;
    try { popup = window.open('', popupName, 'width=520,height=650,left=200,top=100'); } catch (e) { popup = null; }

    var settled = false;
    var pollTimer = null;
    var checkingClosedPopup = false;
    function cleanup() {
      window.removeEventListener('message', onMessage);
      if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
    }
    function finish(ok, err) {
      if (settled) return;
      settled = true;
      cleanup();
      if (err) reject(err); else resolve(ok);
    }

    function onMessage(event) {
      var data = event && event.data;
      if (!data || typeof data !== 'object' || typeof data.type !== 'string') return;
      var ok = data.type.match(/^([a-z]+)-auth-success$/);
      var bad = data.type.match(/^([a-z]+)-auth-error$/);
      if (ok && ok[1] === provider) finish(true);
      else if (bad && bad[1] === provider) finish(false, new Error(data.error || (providerLabel(provider) + ' connection failed.')));
    }
    window.addEventListener('message', onMessage);

    function openInPopup(url) {
      if (popup && !popup.closed) popup.location.href = url;
      else popup = window.open(url, popupName, 'width=520,height=650,left=200,top=100');
      if (!popup) {
        finish(false, new Error('Popup was blocked. Please allow popups for this site and try again.'));
        return;
      }
      // Shopify and other OAuth pages can detach window.opener. If that drops
      // postMessage, confirm whether the server account changed before calling
      // the close a cancellation.
      pollTimer = setInterval(function() {
        if (!popup || !popup.closed || checkingClosedPopup) return;
        checkingClosedPopup = true;
        if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
        Promise.all([
          baselineFingerprint,
          getProviderAccountsFingerprint(provider).catch(function() { return null; })
        ]).then(function(states) {
          var before = states[0];
          var after = states[1];
          if (before !== null && after !== null && before !== after) {
            finish(true);
            return;
          }
          finish(false, new Error('cancelled'));
        });
      }, 600);
    }

    if (directUrl) {
      openInPopup(directUrl);
      return;
    }

    request('/' + provider + '-auth/url' + (query ? ('?' + query) : ''))
      .then(function(result) {
        if (result && result.url) {
          openInPopup(result.url);
        } else {
          if (popup && !popup.closed) popup.close();
          finish(false, new Error(providerLabel(provider) + ' login is not available.'));
        }
      })
      .catch(function(err) {
        if (popup && !popup.closed) popup.close();
        finish(false, err || new Error('Failed to start ' + providerLabel(provider) + ' login.'));
      });
  });
}

// Confirm the provider actually persisted server-side. The popup posts
// "<provider>-auth-success" once the callback saved, but a blocked popup, an
// abandoned flow, or a stale message can leave us thinking we connected when no
// account row exists — which then re-fails the planner probe with a confusing
// "still not connected" loop. Hit GET /connected-accounts and check the array.
// Resolves true only when at least one account exists for the provider.
function verifyProviderConnected(provider) {
  return request('/connected-accounts')
    .then(function(result) {
      var accounts = (result && result.accounts && result.accounts[provider]) || [];
      return Array.isArray(accounts) && accounts.length > 0;
    })
    .catch(function() { return false; });
}

module.exports = {
  connectProvider: connectProvider,
  verifyProviderConnected: verifyProviderConnected,
  providerAccountsFingerprint: providerAccountsFingerprint,
  getProviderAccountsFingerprint: getProviderAccountsFingerprint,
  providerLabel: providerLabel,
  isOAuthProvider: isOAuthProvider,
  PROVIDER_LABELS: PROVIDER_LABELS
};
